/**
 * Phase 290.c — Weekly Goal Sticky Banner
 * ========================================
 * Tasteful one-line reminder of the academy owner's stated goal for THIS
 * week, displayed at the top of `/dashboard`. Two states:
 *
 *   • Has goal  → 🎯 "This week's goal: {text}"  +  small "Edit →" link
 *   • No goal   → 🎯 "Set this week's goal · It takes 30 seconds and
 *                     Claude will measure your progress next Monday."
 *
 * The banner is dismissible PER WEEK (localStorage key includes the ISO
 * week label) so a one-off "Got it" interaction doesn't keep nagging
 * indefinitely, but a NEW week always re-surfaces it.
 *
 * Auto-hides entirely on fetch failure so a backend hiccup never blocks
 * the dashboard from rendering.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Target, Pencil, X } from "lucide-react";

const DISMISS_KEY_PREFIX = "outplay_weekly_goal_banner_dismissed_";

export default function WeeklyGoalBanner() {
  const [state, setState] = useState({ loaded: false, data: null, dismissed: false });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/weekly-goals/current");
        if (cancelled) return;
        const data = res.data;
        const dismissed = !!localStorage.getItem(`${DISMISS_KEY_PREFIX}${data.week_iso}`);
        setState({ loaded: true, data, dismissed });
      } catch (e) {
        // Silent — the dashboard must render even if this endpoint hiccups.
        if (!cancelled) setState({ loaded: true, data: null, dismissed: true });
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = () => {
    if (state.data?.week_iso) {
      localStorage.setItem(`${DISMISS_KEY_PREFIX}${state.data.week_iso}`, "1");
    }
    setState((s) => ({ ...s, dismissed: true }));
  };

  if (!state.loaded || !state.data || state.dismissed) {
    return null;
  }

  const { has_goal, goal_text, goal_capture_url, week_iso } = state.data;

  // Two visual treatments — set vs unset — same shell, different palette.
  const palette = has_goal
    ? {
        bg: "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30",
        border: "border-amber-200 dark:border-amber-900",
        icon: "text-amber-600 dark:text-amber-400",
        kicker: "text-amber-700 dark:text-amber-300",
        body: "text-slate-900 dark:text-slate-100",
        link: "text-amber-700 dark:text-amber-300 hover:text-amber-800 border-amber-400",
      }
    : {
        bg: "bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
        border: "border-emerald-200 dark:border-emerald-900",
        icon: "text-emerald-700 dark:text-emerald-400",
        kicker: "text-emerald-700 dark:text-emerald-300",
        body: "text-slate-700 dark:text-slate-200",
        link: "text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 border-emerald-400",
      };

  return (
    <div
      className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border ${palette.bg} ${palette.border}`}
      data-testid="weekly-goal-banner"
    >
      <div className={`shrink-0 h-9 w-9 rounded-full bg-white/70 dark:bg-slate-900/40 flex items-center justify-center ${palette.icon}`}>
        <Target className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        {has_goal ? (
          <>
            <p
              className={`text-[10px] uppercase tracking-wider font-bold ${palette.kicker} mb-0.5`}
              data-testid="weekly-goal-banner-kicker"
            >
              ⭐ This week's goal · {week_iso}
            </p>
            <p
              className={`text-sm font-medium leading-snug truncate ${palette.body}`}
              data-testid="weekly-goal-banner-text"
              title={goal_text}
            >
              "{goal_text}"
            </p>
          </>
        ) : (
          <>
            <p className={`text-[10px] uppercase tracking-wider font-bold ${palette.kicker} mb-0.5`}>
              🎯 Set this week's goal · {week_iso}
            </p>
            <p className={`text-sm leading-snug ${palette.body}`}>
              Lock in one specific target. Claude will measure your progress in next Monday's digest.
            </p>
          </>
        )}
      </div>

      <a
        href={goal_capture_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border-b border-dashed pb-0.5 ${palette.link}`}
        data-testid="weekly-goal-banner-cta"
      >
        {has_goal ? (
          <>
            <Pencil className="h-3.5 w-3.5" /> Edit goal
          </>
        ) : (
          <>Set my goal →</>
        )}
      </a>

      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 ml-1 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-white/60 dark:hover:bg-slate-800/50 transition-colors"
        aria-label="Dismiss this week's goal banner"
        data-testid="weekly-goal-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
